import React from "react";

import {
  BadgeCheck,
  Flag,
  FolderHeart,
  Heart,
  MessageSquare,
} from "lucide-react";
import moment from "moment";
import { NavLink } from "react-router-dom";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/src/components/ui/avatar";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import { getIcon } from "../../../utils/data/Content";
import DefaultAvatarImage from "../../common/DefaultAvatarImage";
import { CONTENT_ICON_BY_NAME } from "../../content/contentTypeIcons";
import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  countColumn,
  dateTimeColumn,
  selectColumn,
} from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { CELL_ICON_CLASS } from "../../dataTable/dataTableStyles";
import {
  commentViewPath,
  contentPath,
  smartlistEditPath,
  userEditPath,
} from "../../routes/routes";

import type { TFunction } from "i18next";

import type { CommentListItem } from "../../../api/AdminUserAPI.types";
import type {
  DataTableColumnDef,
  RowAction,
} from "../../dataTable/dataTable.types";
import type {
  CommentRow,
  CommentStatus,
  CommentType,
  CommentTypeDisplay,
} from "../comment.types";

interface CommentColumnsOptions {
  t: TFunction;
  projectSlug: string;
  canAccessUsers: boolean;
  getRowActions: (comment: CommentRow) => RowAction[];
}

const TYPE_DISPLAYS: Record<CommentType, CommentTypeDisplay> = {
  videocam: {
    icon: CONTENT_ICON_BY_NAME.videocam,
    labelKey: "editor-content:tooltip_videocam",
  },
  music_note: {
    icon: CONTENT_ICON_BY_NAME.music_note,
    labelKey: "editor-content:tooltip_music_note",
  },
  replay: {
    icon: CONTENT_ICON_BY_NAME.replay,
    labelKey: "editor-content:tooltip_replay",
  },
  live_tv: {
    icon: CONTENT_ICON_BY_NAME.live_tv,
    labelKey: "editor-content:tooltip_live_tv",
  },
  movie: {
    icon: CONTENT_ICON_BY_NAME.movie,
    labelKey: "editor-content:tooltip_movie",
  },
  folder_special: { icon: FolderHeart, labelKey: "editor-content:smartlist" },
};

function isCommentType(icon: string): icon is CommentType {
  return Object.keys(TYPE_DISPLAYS).includes(icon);
}

function getCommentType(comment: CommentListItem): CommentType {
  if (!comment.media) return "folder_special";

  const icon: unknown = getIcon(comment.media);
  return typeof icon === "string" && isCommentType(icon) ? icon : "videocam";
}

function getCommentStatus(comment: CommentListItem): CommentStatus[] {
  const statusList: CommentStatus[] = [];
  if (comment.valid) statusList.push("valid");
  if (comment.comment_abuses > 0) statusList.push("comment_abuses");
  return statusList;
}

export function toCommentRow(comment: CommentListItem): CommentRow {
  return {
    ...comment,
    status: getCommentStatus(comment),
    type: getCommentType(comment),
    source: comment.media?.label ?? comment.smartlist?.label ?? "",
    inserted_at: moment.utc(comment.inserted_at).toISOString(),
  };
}

function getSourceHref(
  comment: CommentRow,
  projectSlug: string,
): string | undefined {
  if (comment.media) return contentPath(comment.media.type, comment.media.uuid);
  return smartlistEditPath(projectSlug, "smartlist", comment.smartlist?.uuid);
}

export function commentColumns({
  t,
  projectSlug,
  canAccessUsers,
  getRowActions,
}: CommentColumnsOptions): DataTableColumnDef<CommentRow>[] {
  return [
    selectColumn<CommentRow>((comment) => comment.comment),
    {
      id: "status",
      accessorFn: (row) => row.status,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("status")} />
      ),
      enableSorting: false,
      meta: {
        label: t("status"),
        width: "75px",
        align: "center",
        filterOptions: [
          { value: "valid", label: t("verified"), icon: <BadgeCheck /> },
          { value: "comment_abuses", label: t("reported"), icon: <Flag /> },
        ],
      },
      cell: ({ row }) => {
        const { comment_abuses: abuses, valid } = row.original;
        const verifiedLabel = t("verified");
        const reportsLabel = t("reportsCount", { count: abuses });
        if (abuses <= 0 && !valid) return null;

        return (
          <span className="inline-flex items-center gap-1 align-middle">
            {abuses > 0 ? (
              <span
                className="inline-flex"
                data-tooltip-content={reportsLabel}
                data-tooltip-id={APP_TOOLTIP_ID}
              >
                <Flag aria-hidden="true" className={CELL_ICON_CLASS} />
                <span className="sr-only">{reportsLabel}</span>
              </span>
            ) : null}
            {valid ? (
              <span
                className="inline-flex"
                data-tooltip-content={verifiedLabel}
                data-tooltip-id={APP_TOOLTIP_ID}
              >
                <BadgeCheck aria-hidden="true" className={CELL_ICON_CLASS} />
                <span className="sr-only">{verifiedLabel}</span>
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      id: "type",
      accessorFn: (row) => row.type,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("type")} />
      ),
      enableSorting: false,
      meta: {
        label: t("type"),
        width: "5%",
        minWidth: 50,
        align: "center",
        filterOptions: Object.entries(TYPE_DISPLAYS).map(
          ([value, { icon: TypeIcon, labelKey }]) => ({
            value,
            label: t(labelKey),
            icon: <TypeIcon />,
          }),
        ),
      },
      cell: ({ row }) => {
        const { icon: TypeIcon, labelKey } = TYPE_DISPLAYS[row.original.type];
        const typeLabel = t(labelKey);

        return (
          <span
            className="inline-flex align-middle"
            data-tooltip-content={typeLabel}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            <TypeIcon aria-hidden="true" className={CELL_ICON_CLASS} />
            <span className="sr-only">{typeLabel}</span>
          </span>
        );
      },
    },
    {
      id: "source",
      accessorFn: (row) => row.source,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("source")} />
      ),
      meta: { label: t("source"), searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={
            canAccessUsers
              ? getSourceHref(row.original, projectSlug)
              : undefined
          }
        >
          {row.original.source}
        </DataTableLinkCell>
      ),
    },
    {
      id: "comment",
      accessorFn: (row) => row.comment,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("preview")} />
      ),
      meta: { label: t("preview"), searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={commentViewPath(projectSlug, row.original.uuid)}
        >
          {row.original.comment}
        </DataTableLinkCell>
      ),
    },
    {
      id: "owner",
      accessorFn: (row) => row.user_avatar_url,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("owner")} />
      ),
      enableSorting: false,
      meta: { label: t("owner"), width: "10%", minWidth: 120, align: "center" },
      cell: ({ row }) => {
        const { user_avatar_url: avatar, user_uuid: userUuid } = row.original;
        const userHref = userEditPath(projectSlug, userUuid);
        const avatarImage = (
          <Avatar>
            <AvatarImage alt="" src={avatar || undefined} />
            <AvatarFallback>
              <DefaultAvatarImage />
            </AvatarFallback>
          </Avatar>
        );

        return canAccessUsers && userHref ? (
          <NavLink
            aria-label={t("owner")}
            className="inline-flex align-middle"
            to={userHref}
          >
            {avatarImage}
          </NavLink>
        ) : (
          <span className="inline-flex align-middle">{avatarImage}</span>
        );
      },
    },
    countColumn<CommentRow>("comment_likes", t("likes"), <Heart />, {
      width: "12%",
      minWidth: 90,
    }),
    countColumn<CommentRow>("responses", t("responses"), <MessageSquare />, {
      width: "12%",
      minWidth: 115,
    }),
    dateTimeColumn<CommentRow>("inserted_at", t("date")),
    actionsColumn<CommentRow>(getRowActions, (comment) => comment.comment),
  ];
}
